import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconButton, TextField, InputAdornment } from "@material-ui/core";
import { CloudDownloadOutlined, RefreshRounded, Search } from "@material-ui/icons";
import api from "../../../services/api";
import CircularLoader from "../../UI/Loader";
import BookingsTable from "./BookingsTable";
import { Pagination } from "@material-ui/lab";
import { COLORS } from "../../../assets/colors";

function Bookings() {
  const [bookings, setBookings] = useState(null);
  const [bookingsCount, setBookingsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 15;
  const limit = 15;
  const offset = (page - 1) * pageSize;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchBookings();
      } else {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchBookings();
  }, [page]);

  async function fetchBookings() {
    try {
      setLoading(true);
      let response;
      if (searchQuery) {
        response = await api.get("/rideRequests/search", {
          params: { q: searchQuery, limit, offset },
        });
      } else {
        response = await api.get("/admin/refunds/bookings", {
          params: { limit, offset },
        });
      }

      if (response.status == 200) {
        setLoading(false);
        setBookings(response.data.data);
        setBookingsCount(response.data.count);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (bookingsCount > 0) {
      setPages(Math.ceil(bookingsCount / pageSize));
    }
  }, [bookingsCount]);

  const handleChange = (event, value) => {
    setPage(value);
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-text">
          <h1>Booking Records</h1>
          <p>Monitor and manage all ride bookings and refund requests.</p>
        </div>
        <HeaderActions>
          <SearchWrapper>
            <TextField
              placeholder="Search by name or location..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: COLORS.adminTextSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </SearchWrapper>
          <IconButton onClick={fetchBookings}>
            <RefreshRounded />
          </IconButton>
          <IconButton className="download-btn">
            <CloudDownloadOutlined />
          </IconButton>
        </HeaderActions>
      </PageHeader>

      <TableCard>
        {loading ? (
          <CircularLoader isLoading={loading} />
        ) : (
          <BookingsTable bookings={bookings} fetchBookings={fetchBookings} />
        )}
      </TableCard>

      <PaginationWrapper>
        {pages > 1 && bookings?.length >= 1 && (
          <Pagination
            onChange={handleChange}
            size="large"
            page={page}
            count={pages}
            color="primary"
          />
        )}
      </PaginationWrapper>
    </PageWrapper>
  );
}

export default Bookings;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-text {
    h1 {
      margin: 0;
      font-size: 1.875rem;
      font-weight: 700;
      color: ${COLORS.adminTextPrimary};
    }
    p {
      margin: 0.5rem 0 0 0;
      color: ${COLORS.adminTextSecondary};
      font-size: 1rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  .download-btn {
    background: ${COLORS.adminAccent};
    color: white;
    &:hover {
      background: ${COLORS.adminAccent};
      opacity: 0.9;
    }
  }
`;

const SearchWrapper = styled.div`
  margin-right: 1rem;
  .MuiOutlinedInput-root {
    background: white;
    border-radius: 8px;
    width: 300px;
    transition: width 0.3s ease;
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.adminAccent};
    }
    
    &.Mui-focused {
      width: 400px;
      .MuiOutlinedInput-notchedOutline {
        border-color: ${COLORS.adminAccent};
        border-width: 2px;
      }
    }
  }
`;

const TableCard = styled.div`
  background: ${COLORS.adminCardBg};
  border-radius: 12px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0 3rem 0;
`;
